import React, { useState, useEffect } from 'react';
import './Settings.css';

const Settings = ({ onBibleVerseToggle, showBibleVerse }) => {
  const [rates, setRates] = useState({
    electricity: {
      fixed_charge: 0,
      tiers: []
    },
    water: {
      meter_rental: 0,
      tiers: []
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allowHistoryModification, setAllowHistoryModification] = useState(
    localStorage.getItem('allowHistoryModification') === 'true'
  );

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/get-rates');
      const result = await response.json();
      if (result.success) {
        setRates(result.data);
      }
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleElectricityChange = (field, value) => {
    setRates(prev => ({
      ...prev,
      electricity: {
        ...prev.electricity,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleElectricityTierChange = (index, field, value) => {
    setRates(prev => {
      const newTiers = [...prev.electricity.tiers];
      newTiers[index] = {
        ...newTiers[index],
        [field]: field === 'rate' ? parseFloat(value) || 0 : value
      };
      return {
        ...prev,
        electricity: {
          ...prev.electricity,
          tiers: newTiers
        }
      };
    });
  };

  const handleWaterChange = (field, value) => {
    setRates(prev => ({
      ...prev,
      water: {
        ...prev.water,
        [field]: parseFloat(value) || 0
      }
    }));
  };

  const handleWaterTierChange = (index, field, value) => {
    setRates(prev => {
      const newTiers = [...prev.water.tiers];
      newTiers[index] = {
        ...newTiers[index],
        [field]: field === 'rate' ? parseFloat(value) || 0 : value
      };
      return {
        ...prev,
        water: {
          ...prev.water,
          tiers: newTiers
        }
      };
    });
  };

  const handleHistoryModificationToggle = () => {
    const newValue = !allowHistoryModification;
    setAllowHistoryModification(newValue);
    localStorage.setItem('allowHistoryModification', newValue.toString());
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/update-rates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rates)
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess('Rates updated successfully!');
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTierRange = (tier, index, isLast) => {
    if (index === 0) {
      return `0 - ${tier.max}`;
    } else if (isLast) {
      return `${tier.min}+`;
    } else {
      return `${tier.min} - ${tier.max}`;
    }
  };

  return (
    <div className="settings-page">
      <h2>Settings</h2>

      <div className="settings-sections">
        {/* Display & History Settings */}
        <div className="settings-section card">
          <h3>Display & History Options</h3>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={showBibleVerse}
                onChange={(e) => onBibleVerseToggle(e.target.checked)}
              />
              Show daily Bible verse
            </label>
            <small className="form-help">
              Display a daily Bible verse in the header
            </small>
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={allowHistoryModification}
                onChange={handleHistoryModificationToggle}
              />
              Enable bill modification
            </label>
            <small className="form-help">
              Allow modifying and regenerating historical bills
            </small>
          </div>
        </div>

        {/* Electricity Rates */}
        <div className="settings-section card">
          <h3>Electricity Rates (Aruban Florin)</h3>
          
          <div className="base-charge-section">
            <div className="form-group">
              <label className="form-label">Monthly Fixed Base Charge (FL)</label>
              <input
                type="number"
                step="0.01"
                className="form-input base-price-input"
                value={loading ? '' : rates.electricity.fixed_charge}
                onChange={(e) => handleElectricityChange('fixed_charge', e.target.value)}
                placeholder="12.50"
                disabled={loading}
              />
              <small className="form-help">
                Fixed monthly charge regardless of consumption (Currently: FL {rates.electricity.fixed_charge})
              </small>
            </div>
          </div>

          <div className="tiers-section">
            <h4>Tiered Rates</h4>
            {rates.electricity.tiers.map((tier, index) => (
              <div key={index} className="tier-row">
                <div className="tier-label">
                  Tier {index + 1}: {formatTierRange(tier, index, index === rates.electricity.tiers.length - 1)} kWh
                </div>
                <div className="tier-input">
                  <label>Rate (FL per kWh)</label>
                  <input
                    type="number"
                    step="0.0001"
                    className="form-input"
                    value={loading ? '' : tier.rate}
                    onChange={(e) => handleElectricityTierChange(index, 'rate', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Water Rates */}
        <div className="settings-section card">
          <h3>Water Rates (Aruban Florin)</h3>
          
          <div className="base-charge-section">
            <div className="form-group">
              <label className="form-label">Monthly Meter Rental (FL)</label>
              <input
                type="number"
                step="0.01"
                className="form-input base-price-input"
                value={loading ? '' : rates.water.meter_rental}
                onChange={(e) => handleWaterChange('meter_rental', e.target.value)}
                placeholder="1.00"
                disabled={loading}
              />
              <small className="form-help">
                Fixed monthly meter rental fee (Currently: FL {rates.water.meter_rental})
              </small>
            </div>
          </div>

          <div className="tiers-section">
            <h4>Tiered Rates</h4>
            {rates.water.tiers.map((tier, index) => (
              <div key={index} className="tier-row">
                <div className="tier-label">
                  Tier {index + 1}: {formatTierRange(tier, index, index === rates.water.tiers.length - 1)} m³
                </div>
                <div className="tier-input">
                  <label>Rate (FL per m³)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={loading ? '' : tier.rate}
                    onChange={(e) => handleWaterTierChange(index, 'rate', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Information */}
        <div className="settings-section card">
          <h3>System Information</h3>
          <div className="system-info">
            <div className="info-item">
              <span>Application:</span>
              <span>Property Utility Calculator</span>
            </div>
            <div className="info-item">
              <span>Version:</span>
              <span>1.1.0</span>
            </div>
            <div className="info-item">
              <span>Currency:</span>
              <span>Aruban Florin (FL)</span>
            </div>
            <div className="info-item">
              <span>Electricity Provider:</span>
              <span>Elmar</span>
            </div>
            <div className="info-item">
              <span>Water Provider:</span>
              <span>WebAruba</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <button
            className="btn btn-primary save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <span className="loading"></span> : '💾'}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;